import { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { openInquiryModal } from '@/components/feature/InquiryModal';
import { supabase } from '@/lib/supabase';

interface SpecGroup {
  category: string;
  items: { label: string; value: string }[];
}

interface ProductFromDB {
  id: string;
  name: string;
  slug: string;
  category: string;
  subcategory: string | null;
  manufacturer: string;
  origin: string;
  price_min: number | null;
  price_max: number | null;
  description: string | null;
  images: string[] | null;
  specs: SpecGroup[] | null;
  applications: string[] | null;
  brochure_url: string | null;
}

interface ProductUI {
  id: string;
  slug: string;
  name: string;
  category: string;
  subcategory: string;
  manufacturer: string;
  origin: string;
  priceRange: string;
  description: string;
  image: string;
  images: string[];
  specs: SpecGroup[];
  applications: string[];
  brochureUrl: string;
}

function formatPriceRange(min: number | null, max: number | null): string {
  if (min && max) return `$${min.toLocaleString()} - $${max.toLocaleString()} USD`;
  if (min) return `From $${min.toLocaleString()} USD`;
  if (max) return `Up to $${max.toLocaleString()} USD`;
  return 'Request a Quote';
}

function toUI(product: ProductFromDB): ProductUI {
  const imgs = product.images || [];
  const placeholder = 'https://readdy.ai/api/search-image?query=Modern%20industrial%20product%20on%20clean%20white%20studio%20background%20professional%20product%20photography%20clean%20lighting%20isolated%20presentation&width=800&height=600&seq=product-detail-placeholder&orientation=landscape';
  return {
    id: product.slug,
    slug: product.slug,
    name: product.name,
    category: product.category,
    subcategory: product.subcategory || '',
    manufacturer: product.manufacturer,
    origin: product.origin,
    priceRange: formatPriceRange(product.price_min, product.price_max),
    description: product.description || '',
    image: imgs.length > 0 ? imgs[0] : placeholder,
    images: imgs.length > 1 ? imgs : [imgs.length > 0 ? imgs[0] : placeholder],
    specs: product.specs || [],
    applications: product.applications || [],
    brochureUrl: product.brochure_url || '#',
  };
}

export default function ProductDetail() {
  const { id: slug } = useParams();
  const [product, setProduct] = useState<ProductUI | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState(0);

  const fetchProduct = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchErr } = await supabase
        .from('products')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();

      if (fetchErr) throw fetchErr;
      if (!data) { setProduct(null); return; }
      setProduct(toUI(data as ProductFromDB));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load product');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  // Related products
  const [relatedProducts, setRelatedProducts] = useState<ProductUI[]>([]);

  useEffect(() => {
    if (!product) return;
    const fetchRelated = async () => {
      try {
        const { data } = await supabase
          .from('products')
          .select('*')
          .eq('category', product.category)
          .neq('slug', product.slug)
          .limit(3)
          .order('created_at', { ascending: false });

        if (data) {
          setRelatedProducts(data.map((p: ProductFromDB) => {
            const imgs = p.images || [];
            const placeholder = 'https://readdy.ai/api/search-image?query=Modern%20industrial%20product%20on%20clean%20white%20studio%20background%20professional%20product%20photography%20clean%20lighting%20isolated%20presentation&width=800&height=600&seq=related-placeholder&orientation=landscape';
            return {
              id: p.slug,
              slug: p.slug,
              name: p.name,
              category: p.category,
              subcategory: p.subcategory || '',
              manufacturer: p.manufacturer,
              origin: p.origin,
              priceRange: formatPriceRange(p.price_min, p.price_max),
              description: p.description || '',
              image: imgs.length > 0 ? imgs[0] : placeholder,
              images: imgs,
              specs: p.specs || [],
              applications: p.applications || [],
              brochureUrl: p.brochure_url || '#',
            };
          }));
        }
      } catch {
        // Silently fail for related products
      }
    };
    fetchRelated();
  }, [product]);

  // Loading state
  if (loading) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen bg-background-50 pt-[80px] flex items-center justify-center">
          <div className="w-10 h-10 border-2 border-accent-500 border-t-transparent rounded-full animate-spin" />
        </main>
        <Footer />
      </>
    );
  }

  // Error state
  if (error) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen bg-background-50 pt-[80px] flex items-center justify-center">
          <div className="text-center px-6">
            <div className="w-20 h-20 mx-auto mb-5 flex items-center justify-center rounded-full bg-red-50 text-red-400">
              <i className="ri-error-warning-line text-3xl"></i>
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-3">Something went wrong</h1>
            <p className="text-sm text-foreground-500 mb-6 max-w-md mx-auto">{error}</p>
            <button onClick={fetchProduct} className="inline-flex items-center gap-2 px-6 py-2.5 bg-accent-500 text-primary-900 font-semibold text-sm rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap cursor-pointer">
              <i className="ri-refresh-line"></i> Try Again
            </button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  // Not found
  if (!product) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen bg-background-50 pt-[80px] flex items-center justify-center">
          <div className="text-center px-6">
            <div className="w-20 h-20 mx-auto mb-5 flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
              <i className="ri-error-warning-line text-3xl"></i>
            </div>
            <h1 className="text-3xl font-heading font-bold text-foreground-950 mb-3">Product Not Found</h1>
            <p className="text-sm text-foreground-500 mb-6 max-w-md mx-auto">The product you are looking for may have been removed or the link is incorrect.</p>
            <Link to="/products" className="inline-flex items-center gap-2 px-6 py-2.5 bg-accent-500 text-primary-900 font-semibold text-sm rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap cursor-pointer">
              <i className="ri-arrow-left-line"></i> Back to Products
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-background-50 pt-[80px]">
        {/* Breadcrumb */}
        <div className="w-full px-6 md:px-10 lg:px-16 py-5 bg-background-100 border-b border-background-200/70">
          <div className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-foreground-300"></i>
            <Link to="/products" className="hover:text-primary-600 transition-colors">Products</Link>
            <i className="ri-arrow-right-s-line text-foreground-300"></i>
            <Link to={`/products?category=${encodeURIComponent(product.category)}`} className="hover:text-primary-600 transition-colors">{product.category}</Link>
            <i className="ri-arrow-right-s-line text-foreground-300"></i>
            <span className="text-foreground-700 font-medium">{product.name}</span>
          </div>
        </div>

        {/* Product Hero */}
        <div className="w-full px-6 md:px-10 lg:px-16 py-10 md:py-14">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-14">
            {/* Image Gallery */}
            <div className="w-full lg:w-7/12">
              <div className="rounded-2xl overflow-hidden bg-background-100 border border-background-200/70 aspect-[4/3] mb-4">
                <img
                  src={product.images[selectedImage] || product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
              {product.images.length > 1 && (
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {product.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition-all duration-200 cursor-pointer ${
                        selectedImage === idx ? 'border-accent-500' : 'border-background-200/70 hover:border-accent-300'
                      }`}
                    >
                      <img src={img} alt={`${product.name} view ${idx + 1}`} className="w-full h-full object-cover" loading="lazy" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="w-full lg:w-5/12">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-accent-600 bg-accent-50 px-2.5 py-1 rounded-full">{product.origin}</span>
                <span className="text-[10px] text-foreground-400">{product.manufacturer}</span>
              </div>

              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3 leading-tight">{product.name}</h1>
              <p className="text-xs text-foreground-400 mb-5">{product.category} &rsaquo; {product.subcategory}</p>

              <div className="text-2xl font-heading font-bold text-primary-600 mb-5">{product.priceRange}</div>

              <p className="text-sm text-foreground-600 leading-relaxed mb-7">{product.description}</p>

              {/* Applications */}
              {product.applications.length > 0 && (
                <div className="mb-7">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-3">Applications</h4>
                  <div className="flex flex-wrap gap-2">
                    {product.applications.map((app) => (
                      <span key={app} className="text-xs px-3 py-1.5 bg-background-100 border border-background-200/70 rounded-full text-foreground-600">{app}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 mb-6">
                <button onClick={() => openInquiryModal(product.name)} className="px-6 py-3 bg-accent-500 text-primary-900 font-semibold text-sm rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2">
                  <i className="ri-file-text-line"></i> Request a Quote
                </button>
                <a href={`https://wa.me/254700000000?text=I'm%20interested%20in%20${encodeURIComponent(product.name)}`} target="_blank" rel="nofollow noopener noreferrer" className="px-6 py-3 bg-[#25D366] text-white font-semibold text-sm rounded-full hover:bg-[#1ea952] transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2">
                  <i className="ri-whatsapp-line"></i> WhatsApp
                </a>
                <button onClick={() => openInquiryModal(product.name)} className="px-6 py-3 bg-background-100 border border-background-200 text-foreground-700 font-semibold text-sm rounded-full hover:bg-background-200 transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2">
                  <i className="ri-mail-line"></i> Email Inquiry
                </button>
              </div>

              {/* Brochure Download */}
              {product.brochureUrl && product.brochureUrl !== '#' ? (
                <a
                  href={product.brochureUrl}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                >
                  <i className="ri-download-2-line"></i> Download Brochure (PDF)
                </a>
              ) : (
                <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-background-100 border border-background-200 text-foreground-400 text-sm font-medium rounded-full whitespace-nowrap cursor-not-allowed">
                  <i className="ri-download-2-line"></i> Brochure Coming Soon
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Full Specifications */}
        {product.specs.length > 0 && (
          <div className="w-full px-6 md:px-10 lg:px-16 py-12 md:py-16 bg-background-100">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8">Specifications</h2>
              <div className="space-y-5">
                {product.specs.map((group, gIdx) => (
                  <div key={gIdx} className="bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden">
                    <div className="px-6 py-3.5 bg-background-100/60 border-b border-background-200/60">
                      <h3 className="text-sm font-heading font-semibold text-foreground-900">{group.category}</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[500px]">
                        <tbody>
                          {group.items.map((item, iIdx) => (
                            <tr key={iIdx} className={iIdx % 2 === 0 ? 'bg-transparent' : 'bg-background-100/40'}>
                              <td className="px-6 py-3 text-sm font-medium text-foreground-700 w-1/3">{item.label}</td>
                              <td className="px-6 py-3 text-sm text-foreground-600">{item.value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="w-full px-6 md:px-10 lg:px-16 py-14 md:py-20">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
              <div>
                <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-600 mb-3">Explore More</span>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">Related Products</h2>
              </div>
              <Link to={`/products?category=${encodeURIComponent(product.category)}`} className="inline-flex items-center gap-2 text-sm font-semibold text-accent-600 hover:text-accent-500 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                View All in {product.category} <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedProducts.map((rp) => (
                <Link
                  key={rp.id}
                  to={`/products/${rp.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden hover:border-accent-200/50 transition-all duration-400 cursor-pointer block"
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-background-100">
                    <img src={rp.image} alt={rp.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                    <span className="absolute top-3 left-3 px-2.5 py-1 bg-background-50/90 backdrop-blur-sm text-[10px] font-semibold uppercase tracking-wider text-foreground-600 rounded-full">
                      {rp.subcategory}
                    </span>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[10px] font-medium text-accent-600 bg-accent-50 px-2 py-0.5 rounded-full uppercase">{rp.origin}</span>
                      <span className="text-[10px] text-foreground-400">{rp.manufacturer}</span>
                    </div>
                    <h3 className="text-sm font-heading font-semibold text-foreground-900 mb-1.5 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">{rp.name}</h3>
                    <p className="text-xs text-foreground-500 mb-4 line-clamp-2 leading-relaxed">{rp.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-heading font-bold text-primary-600">{rp.priceRange}</span>
                      <span className="text-xs font-medium text-accent-600 group-hover:translate-x-1 transition-transform duration-200 flex items-center gap-1 whitespace-nowrap">
                        Details <i className="ri-arrow-right-s-line"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* CTA Banner */}
        <div className="w-full px-6 md:px-10 lg:px-16 py-14 bg-primary-900 text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">Interested in This Product?</h2>
          <p className="text-sm text-white/60 max-w-xl mx-auto mb-6">Our team can provide detailed pricing, availability, and shipping timelines tailored to your requirements.</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button onClick={() => openInquiryModal(product.name)} className="px-7 py-3 bg-accent-500 text-primary-900 font-semibold text-sm rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap cursor-pointer">
              Request a Quote
            </button>
            <a href="https://wa.me/254700000000" target="_blank" rel="nofollow noopener noreferrer" className="px-7 py-3 bg-white/10 text-white font-semibold text-sm rounded-full hover:bg-white/20 border border-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer">
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}